import { HttpErrorResponse } from '@angular/common/http';

import { ActionScopedPayload, PaginatedActionScopedPayload, PaginationParams } from '../types';
import { pipeState } from '../utils';
import {
  GenericEntity,
  DefaultIdSelector,
  EntityCollectionAdapter,
  EntityPartial,
  EntityState,
  ID,
  GenericIdSelector,
  GenericMetaOptions,
  Scope,
} from './types';
import { resolveScope } from './utils';

/* eslint-disable max-lines */

export type EntityUpdateOperationPayload<Entity extends GenericEntity> =
  EntityOperationAsyncPayload<EntityPartial<Entity>>;

export type EntityOperationSyncPayload<Entity> = {
  data: Entity;
};

export type EntityOperationAsyncPayload<Entity> = {
  data: Entity;
  correlationId: string;
};

type InferIdOnlyType<IdSelector> = IdSelector extends GenericIdSelector<infer R> ? R : never;
type PartialEntity<
  IdSelector extends GenericIdSelector<any>,
  Entity,
> = InferIdOnlyType<IdSelector> & Partial<Entity>;

export interface EntityCollectionActions<
  Entity extends GenericEntity,
  IdSelector extends DefaultIdSelector<Entity>,
  MetaOptions extends GenericMetaOptions,
> {
  fetchOne: (
    state: EntityState<Entity, MetaOptions>,
    { id }: { id: ID },
  ) => EntityState<Entity, MetaOptions>;
  fetchOneFailed: (
    state: EntityState<Entity, MetaOptions>,
    { id, error }: { id: ID; error: HttpErrorResponse },
  ) => EntityState<Entity, MetaOptions>;
  fetchOneSuccess: (
    state: EntityState<Entity, MetaOptions>,
    { data }: { data: Entity },
  ) => EntityState<Entity, MetaOptions>;

  fetchCollection: (
    state: EntityState<Entity, MetaOptions>,
    payload?: { scope: Scope },
  ) => EntityState<Entity, MetaOptions>;
  fetchCollectionSuccess: (
    state: EntityState<Entity, MetaOptions>,
    { data, scope }: ActionScopedPayload<Entity[]>,
  ) => EntityState<Entity, MetaOptions>;
  fetchCollectionFailed: (
    state: EntityState<Entity, MetaOptions>,
    payload: { error: HttpErrorResponse; scope?: Scope },
  ) => EntityState<Entity, MetaOptions>;

  fetchPaginatedCollection: <U extends PaginationParams>(
    state: EntityState<Entity, MetaOptions>,
    payload: { scope: Scope; params: U; add?: boolean },
  ) => EntityState<Entity, MetaOptions>;
  fetchPaginatedCollectionSuccess: (
    state: EntityState<Entity, MetaOptions>,
    { data, scope, add }: PaginatedActionScopedPayload<Entity[]>,
  ) => EntityState<Entity, MetaOptions>;
  fetchPaginatedCollectionFailed: (
    state: EntityState<Entity, MetaOptions>,
    payload: { error: HttpErrorResponse; scope?: Scope },
  ) => EntityState<Entity, MetaOptions>;

  createOne: (
    state: EntityState<Entity, MetaOptions>,
    payload: EntityOperationSyncPayload<Entity> & ActionScopedPayload<Entity>,
  ) => EntityState<Entity, MetaOptions>;

  createOneOptimistic: (
    state: EntityState<Entity, MetaOptions>,
    payload: EntityOperationAsyncPayload<Partial<Entity>> & ActionScopedPayload<Partial<Entity>>,
  ) => EntityState<Entity, MetaOptions>;
  createOneOptimisticSuccess: (
    state: EntityState<Entity, MetaOptions>,
    payload: EntityOperationAsyncPayload<Entity> & ActionScopedPayload<Entity>,
  ) => EntityState<Entity, MetaOptions>;
  createOneOptimisticFailed: (
    state: EntityState<Entity, MetaOptions>,
    payload: EntityOperationAsyncPayload<Entity> &
      ActionScopedPayload<Entity> & { error: HttpErrorResponse },
  ) => EntityState<Entity, MetaOptions>;

  createOnePessimistic: (
    state: EntityState<Entity, MetaOptions>,
    payload:
      | (EntityOperationAsyncPayload<Entity> & ActionScopedPayload<Entity>)
      | (EntityOperationAsyncPayload<Partial<Entity>> & ActionScopedPayload<Partial<Entity>>),
  ) => EntityState<Entity, MetaOptions>;
  createOnePessimisticSuccess: (
    state: EntityState<Entity, MetaOptions>,
    payload: EntityOperationAsyncPayload<Entity> & ActionScopedPayload<Entity>,
  ) => EntityState<Entity, MetaOptions>;
  createOnePessimisticFailed: (
    state: EntityState<Entity, MetaOptions>,
    payload: { error: HttpErrorResponse; correlationId: string; scope?: Scope },
  ) => EntityState<Entity, MetaOptions>;

  updateOne: (
    state: EntityState<Entity, MetaOptions>,
    payload: EntityOperationSyncPayload<Entity>,
  ) => EntityState<Entity, MetaOptions>;

  updateOneOptimistic: (
    state: EntityState<Entity, MetaOptions>,
    payload: EntityOperationAsyncPayload<Entity>,
  ) => EntityState<Entity, MetaOptions>;
  updateOneOptimisticSuccess: (
    state: EntityState<Entity, MetaOptions>,
    { data, correlationId }: { data: Entity } & EntityUpdateOperationPayload<Entity>,
  ) => EntityState<Entity, MetaOptions>;
  updateOneOptimisticFailed: (
    state: EntityState<Entity, MetaOptions>,
    {
      data,
      correlationId,
      error,
    }: EntityUpdateOperationPayload<Entity> & { error: HttpErrorResponse },
  ) => EntityState<Entity, MetaOptions>;

  updateOnePessimistic: (
    state: EntityState<Entity, MetaOptions>,
    payload: { data: PartialEntity<IdSelector, Entity> },
  ) => EntityState<Entity, MetaOptions>;
  updateOnePessimisticSuccess: (
    state: EntityState<Entity, MetaOptions>,
    { data }: { data: Entity },
  ) => EntityState<Entity, MetaOptions>;
  updateOnePessimisticFailed: (
    state: EntityState<Entity, MetaOptions>,
    { data, error }: { data: Entity; error: HttpErrorResponse },
  ) => EntityState<Entity, MetaOptions>;

  removeOne: (
    state: EntityState<Entity, MetaOptions>,
    payload: EntityOperationSyncPayload<ID> & ActionScopedPayload<ID>,
  ) => EntityState<Entity, MetaOptions>;

  removeOneOptimistic: (
    state: EntityState<Entity, MetaOptions>,
    payload: { data: ID },
  ) => EntityState<Entity, MetaOptions>;
  removeOneOptimisticSuccess: (
    state: EntityState<Entity, MetaOptions>,
    { data }: { data: Entity },
  ) => EntityState<Entity, MetaOptions>;
  removeOneOptimisticFailed: (
    state: EntityState<Entity, MetaOptions>,
    { data: id, error }: { data: ID } & { error: HttpErrorResponse },
  ) => EntityState<Entity, MetaOptions>;

  removeOnePessimistic: (
    state: EntityState<Entity, MetaOptions>,
    payload: { data: ID },
  ) => EntityState<Entity, MetaOptions>;
  removeOnePessimisticSuccess: (
    state: EntityState<Entity, MetaOptions>,
    { data }: { data: Entity },
  ) => EntityState<Entity, MetaOptions>;
  removeOnePessimisticFailed: (
    state: EntityState<Entity, MetaOptions>,
    { data: id, error }: { data: ID } & { error: HttpErrorResponse },
  ) => EntityState<Entity, MetaOptions>;
}

export type CreateOneOptimisticPayload<T extends GenericEntity> = EntityOperationAsyncPayload<T> &
  ActionScopedPayload<T>;

/* eslint-disable max-lines */
// eslint-disable-next-line max-lines-per-function
export const getEntityCollectionActions = <
  Entity extends GenericEntity,
  IdSelector extends DefaultIdSelector<Entity>,
  MetaOptions extends GenericMetaOptions,
>(
  adapter: EntityCollectionAdapter<Entity, MetaOptions>,
  selectId: IdSelector,
): EntityCollectionActions<Entity, IdSelector, MetaOptions> => ({
  fetchOne: (state, { id }) => adapter.markAsPending(state, { scope: null, entities: [id] }),
  fetchOneSuccess: (state, { data }) =>
    pipeState(
      state,
      next => adapter.writeOne(next, data),
      next =>
        adapter.markAsLoaded(next, {
          entities: [data.id],
          scope: null,
        }),
    ),
  fetchOneFailed: (state, { id, error }) =>
    adapter.setError(state, error, {
      scope: null,
      entities: [id],
    }),

  fetchCollection: (state, payload) =>
    adapter.markAsPending(state, { scope: resolveScope(payload?.scope), entities: [] }),
  fetchCollectionSuccess: (state, { data, scope }) =>
    pipeState(
      state,
      next => adapter.setMany(next, data, { scope }),
      next =>
        adapter.markAsLoaded(next, {
          scope: resolveScope(scope),
          entities: data.map(e => selectId(e)),
        }),
    ),
  fetchCollectionFailed: (state, payload) =>
    pipeState(
      state,
      next => adapter.markAsLoaded(next, { scope: resolveScope(payload.scope), entities: [] }),
      next =>
        adapter.setError(next, payload.error, {
          scope: resolveScope(payload.scope),
          entities: [],
        }),
    ),

  fetchPaginatedCollection: (state, payload) =>
    adapter.markAsPending(state, { scope: resolveScope(payload?.scope), entities: [] }),
  fetchPaginatedCollectionSuccess: (state, { data, scope, add }) =>
    pipeState(
      state,
      next =>
        add
          ? adapter.addMany(next, data.rows, { scope })
          : adapter.setMany(next, data.rows, { scope }),
      next => adapter.setPaginationMeta(next, data.meta, { scope }),
      next =>
        adapter.markAsLoaded(next, {
          scope,
          entities: data.rows.map(e => e.id),
        }),
    ),
  fetchPaginatedCollectionFailed: (state, payload) =>
    pipeState(
      state,
      next => adapter.markAsLoaded(next, { scope: resolveScope(payload.scope), entities: [] }),
      next =>
        adapter.setError(next, payload.error, {
          scope: resolveScope(payload.scope),
          entities: [],
        }),
    ),

  createOne: (state, payload) =>
    pipeState(
      state,
      next =>
        adapter.markAsLoaded(next, {
          entities: [selectId(payload.data)],
          scope: null,
        }),
      next =>
        adapter.addOne(next, payload.data, {
          scope: payload.scope,
        }),
    ),

  createOneOptimistic: (state, payload) =>
    pipeState(
      state,
      next =>
        adapter.markAsPending(next, {
          entities: [payload.correlationId],
          scope: null,
        }),
      next =>
        adapter.addOne(next, <Entity>payload.data, {
          scope: payload.scope,
          correlationId: payload.correlationId,
        }),
    ),
  createOneOptimisticSuccess: (state, payload) =>
    pipeState(
      state,
      next => adapter.replaceOne(next, payload.correlationId, payload.data),
      next =>
        adapter.markAsLoaded(next, {
          entities: [selectId(payload.data)],
          scope: null,
        }),
      next =>
        adapter.setAlias(next, {
          [payload.correlationId]: selectId(payload.data),
        }),
    ),
  createOneOptimisticFailed: (state, payload) =>
    pipeState(
      state,
      next => adapter.removeOne(next, payload.correlationId),
      next =>
        adapter.setError(next, payload.error, { scope: null, entities: [payload.correlationId] }),
    ),

  createOnePessimistic: (state, payload) => {
    const id = payload.correlationId || selectId(<Entity>payload.data);

    if (!id) {
      throw new Error('key of entity could not be determined');
    }

    return adapter.markAsPending(state, {
      entities: [id],
      scope: null,
    });
  },
  createOnePessimisticSuccess: (state, payload) => {
    const id = selectId(payload.data);

    return pipeState(
      state,
      next =>
        adapter.markAsLoaded(next, {
          entities: [id],
          scope: null,
        }),
      next =>
        payload.correlationId
          ? pipeState(
              next,
              next => adapter.removeOne(next, payload.correlationId),
              next =>
                adapter.setAlias(next, {
                  [payload.correlationId]: id,
                }),
            )
          : next,
      next => adapter.addOne(next, { ...payload.data }, { scope: payload.scope }),
    );
  },
  createOnePessimisticFailed: (state, payload) =>
    adapter.setError(state, payload.error, { entities: [payload.correlationId], scope: null }),

  updateOne: (state, payload) => adapter.updateOne(state, payload.data),

  updateOneOptimistic: (state, payload) =>
    pipeState(
      state,
      next =>
        adapter.optimisticUpdateSet(next, payload.correlationId, {
          before: state.entityMap[selectId(payload.data)]!,
          update: payload.data,
        }),
      next => adapter.updateOne(next, payload.data),
      next =>
        adapter.markAsPending(next, {
          entities: [selectId(payload.data)],
          scope: null,
        }),
    ),
  updateOneOptimisticSuccess: (state, { data, correlationId }) =>
    pipeState(
      state,
      next => adapter.markAsLoaded(next, { entities: [selectId(data)], scope: null }),
      next => adapter.optimisticUpdateRemove(next, correlationId),
      next => adapter.updateOne(next, data),
    ),
  updateOneOptimisticFailed: (state, { data, correlationId, error }) => {
    const transaction = state.optimisticUpdate[correlationId];

    const rollbackUpdate = <EntityPartial<Entity>>(
      Object.fromEntries(
        Object.keys(transaction.update).map(updatedKey => [
          updatedKey,
          transaction.before[<keyof Entity>updatedKey],
        ]),
      )
    );

    return pipeState(
      state,
      next => adapter.optimisticUpdateRemove(next, correlationId),
      next =>
        adapter.setError(next, error, { entities: [selectId(transaction.before)], scope: null }),
      next => adapter.updateOne(next, rollbackUpdate),
    );
  },

  updateOnePessimistic: (state, payload) =>
    adapter.markAsPending(state, {
      entities: [selectId(payload.data)],
      scope: null,
    }),
  updateOnePessimisticSuccess: (state, { data }) =>
    pipeState(
      state,
      next => adapter.markAsLoaded(next, { entities: [selectId(data)], scope: null }),
      next => adapter.updateOne(next, data),
    ),
  updateOnePessimisticFailed: (state, { data, error }) =>
    adapter.setError(state, error, { entities: [selectId(data)], scope: null }),

  removeOne: (state, payload) => adapter.removeOne(state, payload.data),

  removeOneOptimistic: (state, payload) => {
    const before = state.entityMap[payload.data]!;

    return pipeState(
      state,
      next => adapter.markAsPending(next, { entities: [payload.data], scope: null }),
      next => adapter.optimisticDeleteSet(next, before),
      next => adapter.removeOne(next, payload.data),
    );
  },
  removeOneOptimisticSuccess: (state, { data }) =>
    pipeState(state, next => adapter.optimisticDeleteRemove(next, data.id)),
  removeOneOptimisticFailed: (state, { data: id, error }) => {
    const entity = state.optimisticDelete[id];

    return pipeState(
      state,
      next => adapter.optimisticDeleteRemove(next, id),
      next => adapter.setError(next, error, { entities: [id], scope: null }),
      next => adapter.addOne(next, entity),
    );
  },

  removeOnePessimistic: (state, payload) =>
    adapter.markAsPending(state, { entities: [payload.data], scope: null }),
  removeOnePessimisticSuccess: (state, { data }) => adapter.removeOne(state, data.id),
  removeOnePessimisticFailed: (state, { data: id, error }) =>
    adapter.setError(state, error, { entities: [id], scope: null }),
});
